/**
 * Клиентский порт логики повторяющихся задач (сервер: services/recurrence.ts).
 * Календарные даты — YYYY-MM-DD. Дни недели: 0 = понедельник … 6 = воскресенье.
 * Пропущенные повторения (раньше from_date) не возвращаются.
 */

import { addDaysIso, addMonthsCalendar, weekdayMon0 } from '../../../shared/lib/datetime';

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays?: number[];
  end_date?: string | null;
}

const MAX_OCCURRENCES = 200;
const MAX_ITERATIONS = 800;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;

export function isCalendarDate(value: string): boolean {
  return DATE_RE.test(value);
}

function startOfWeekMonday(dateIso: string): string {
  return addDaysIso(dateIso, -weekdayMon0(dateIso));
}

function calendarDaysBetween(a: string, b: string): number {
  const parse = (value: string): number => {
    const [y, m, d] = value.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined) {
      return 0;
    }
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((parse(b) - parse(a)) / 86_400_000);
}

export function normalizeRule(rule: RecurrenceRule): RecurrenceRule {
  const interval = Number.isInteger(rule.interval) ? rule.interval : 1;
  if (interval < 1) {
    throw new Error('Интервал повторения должен быть целым числом >= 1');
  }

  const normalized: RecurrenceRule = {
    frequency: rule.frequency,
    interval,
  };

  if (rule.frequency === 'weekly') {
    const cleaned = [...new Set(rule.weekdays ?? [])]
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
      .sort((a, b) => a - b);
    if (cleaned.length > 0) {
      normalized.weekdays = cleaned;
    }
  }

  if (rule.end_date != null && rule.end_date !== '') {
    if (!isCalendarDate(rule.end_date)) {
      throw new Error('Дата окончания повторения должна быть YYYY-MM-DD');
    }
    normalized.end_date = rule.end_date;
  } else {
    normalized.end_date = null;
  }

  return normalized;
}

export function parseRecurrenceRule(text: string | null | undefined): RecurrenceRule | null {
  if (text == null || text.trim().length === 0) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const frequency = obj['frequency'];
  if (frequency !== 'daily' && frequency !== 'weekly' && frequency !== 'monthly') {
    return null;
  }
  const intervalRaw = obj['interval'];
  const interval =
    typeof intervalRaw === 'number' && Number.isFinite(intervalRaw) ? Math.trunc(intervalRaw) : 1;
  if (interval < 1) {
    return null;
  }

  const weekdaysRaw = obj['weekdays'];
  const weekdays = Array.isArray(weekdaysRaw)
    ? weekdaysRaw.filter((value): value is number => typeof value === 'number')
    : undefined;

  const endRaw = obj['end_date'];
  const end_date = typeof endRaw === 'string' && endRaw.length > 0 ? endRaw : null;

  try {
    return normalizeRule({ frequency, interval, weekdays, end_date });
  } catch {
    return null;
  }
}

export function serializeRecurrenceRule(rule: RecurrenceRule): string {
  const normalized = normalizeRule(rule);
  return JSON.stringify({
    frequency: normalized.frequency,
    interval: normalized.interval,
    ...(normalized.frequency === 'weekly' ? { weekdays: normalized.weekdays ?? [] } : {}),
    end_date: normalized.end_date ?? null,
  });
}

export interface CalculateOccurrencesOptions {
  rule: RecurrenceRule;
  start_date: string;
  from_date: string;
  limit?: number;
}

export function calculateOccurrences(options: CalculateOccurrencesOptions): string[] {
  const limit = Math.min(Math.max(options.limit ?? MAX_OCCURRENCES, 1), MAX_OCCURRENCES);
  const rule = normalizeRule(options.rule);
  const start = options.start_date;
  const from = options.from_date;
  const end = rule.end_date;

  if (!isCalendarDate(start) || !isCalendarDate(from)) {
    return [];
  }
  if (end != null && end < start) {
    return [];
  }

  const out: string[] = [];
  const pushIfInWindow = (date: string): boolean => {
    if (date < from) {
      return false;
    }
    if (end != null && date > end) {
      return false;
    }
    out.push(date);
    return out.length < limit;
  };

  if (rule.frequency === 'daily') {
    for (let i = 0; i < MAX_ITERATIONS; i += 1) {
      const date = addDaysIso(start, i * rule.interval);
      if (end != null && date > end) {
        break;
      }
      if (date >= from) {
        if (!pushIfInWindow(date)) {
          break;
        }
      }
      if (out.length >= limit && date > from) {
        break;
      }
    }
    return out;
  }

  if (rule.frequency === 'weekly') {
    const weekdays = rule.weekdays ?? [weekdayMon0(start)];
    const weekStart0 = startOfWeekMonday(start);
    for (let i = 0; i < MAX_ITERATIONS; i += 1) {
      const date = addDaysIso(start, i);
      if (end != null && date > end) {
        break;
      }
      const wd = weekdayMon0(date);
      if (weekdays.includes(wd)) {
        const weekStart = startOfWeekMonday(date);
        const weekDiff = calendarDaysBetween(weekStart0, weekStart) / 7;
        if (Number.isInteger(weekDiff) && weekDiff % rule.interval === 0 && date >= from) {
          if (!pushIfInWindow(date)) {
            break;
          }
        }
      }
      if (out.length >= limit) {
        break;
      }
    }
    return out;
  }

  // monthly
  const [startY, startM, startD] = start.split('-').map(Number);
  if (startY === undefined || startM === undefined || startD === undefined) {
    return [];
  }
  const startTotalMonths = startY * 12 + (startM - 1);
  for (let k = 0; k < MAX_ITERATIONS; k += 1) {
    const totalMonths = startTotalMonths + k * rule.interval;
    const y = Math.floor(totalMonths / 12);
    const m = (totalMonths % 12) + 1;
    const dim = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const d = Math.min(startD, dim);
    const date = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (end != null && date > end) {
      break;
    }
    if (date >= from) {
      if (!pushIfInWindow(date)) {
        break;
      }
    }
    if (out.length >= limit) {
      break;
    }
  }
  return out;
}

export function isRecurringRuleText(text: string | null | undefined): boolean {
  return parseRecurrenceRule(text) !== null;
}

/** Есть ли дата in_date в серии (для отображения в списках). */
export function occursOnDate(
  ruleText: string | null | undefined,
  startDate: string | null,
  targetDate: string,
): boolean {
  const rule = parseRecurrenceRule(ruleText);
  if (rule === null || startDate === null) {
    return false;
  }
  return calculateOccurrences({
    rule,
    start_date: startDate,
    from_date: targetDate,
    limit: 1,
  })[0] === targetDate;
}

/** Для восстановления серии: новая дата начала = max(today, старая start). */
export function restoreRecurrenceStartDate(
  ruleText: string | null | undefined,
  currentStart: string | null,
  today: string,
): string | null {
  const rule = parseRecurrenceRule(ruleText);
  if (rule === null) {
    return null;
  }
  if (currentStart === null) {
    return today;
  }
  return currentStart < today ? today : currentStart;
}

export { addMonthsCalendar };
